<script setup lang="ts">
import { ref, watch } from 'vue'
import { storeToRefs } from 'pinia'

import RHDHVButton from '@/components/common/RHDHVButton.vue'

import { useShipStore } from '@/stores/ship'
import { useFairleadStore } from '@/stores/fairlead'
import { useCalculationStore } from '@/stores/calculation'

const shipStore = useShipStore()
const fairleadStore = useFairleadStore()
const calculationStore = useCalculationStore()

const { loading, selectedItems, itemsAvailable } = storeToRefs(shipStore)

const { currentItem: currentCalculation } = storeToRefs(calculationStore)
const { editedItems, fetchLoading } = storeToRefs(fairleadStore)

const refresh = ref(false)

watch(
  () => editedItems.value.length,
  (value) => {
    if (value > 0) {
      refresh.value = true
    }
  }
)

const refreshTable = async () => {
  if (currentCalculation.value) {
    await fairleadStore.fetchItems({ ship__calculation: currentCalculation.value.id })
    fairleadStore.emptyEditedItems()
    refresh.value = false
  }
}

const emit = defineEmits<{
  (e: 'setAddDialog', value: boolean): void
  (e: 'setExportDialog', value: boolean): void
}>()
</script>

<template>
  <div class="pa-2" style="text-align: end">
    <RHDHVButton
      class="ml-2 text-none"
      allowed="add_ship"
      auth-source="ProjectUser"
      variant="tonal"
      prepend-icon="mdi-plus"
      size="default"
      @click="emit('setAddDialog', true)"
    >
      Add ship
    </RHDHVButton>
    <RHDHVButton
      :disabled="!itemsAvailable"
      class="ml-2 text-none"
      allowed="view_ship"
      auth-source="ProjectUser"
      variant="tonal"
      prepend-icon="mdi-export"
      size="default"
      @click="emit('setExportDialog', true)"
    >
      Export excel
    </RHDHVButton>
    <RHDHVButton
      :disabled="!itemsAvailable || selectedItems.length === 0"
      class="ml-2 text-none"
      allowed="delete_ship"
      auth-source="ProjectUser"
      variant="tonal"
      prepend-icon="mdi-delete-outline"
      size="default"
      @click="shipStore.toggleDeleteDialog()"
    >
      Delete ship
    </RHDHVButton>
  </div>

  <v-alert v-if="refresh" type="warning" variant="tonal" class="ma-1" density="compact">
    The fairlead table was updated. Maybe the order of fairlead row has changed, please
    <RHDHVButton
      :disabled="!itemsAvailable"
      :loading="fetchLoading"
      class="text-none"
      allowed="view_fairlead"
      auth-source="ProjectUser"
      variant="tonal"
      prepend-icon="mdi-refresh"
      @click="refreshTable()"
    >
      Refresh
    </RHDHVButton>
    the table to update editing.
  </v-alert>

  <div v-if="!itemsAvailable && !loading" class="pa-3" style="font-size: 14px">
    There are no design vessels available, please add a ship via the
    <v-btn
      class="mx-1 clickable text-none"
      style="cursor: default"
      :elevation="0"
      readonly
      :ripple="false"
      prepend-icon="mdi-plus"
      color="primary"
      size="small"
      variant="tonal"
    >
      Add ship
    </v-btn>
    button.
  </div>
</template>
